/**
 * domSafePatches — Patch DOM mutation methods to swallow benign
 * "node not a child" errors on mobile browsers.
 *
 * Why
 * ---
 * React 18 portals (Radix UI Dialog / Select / Popover, react-day-picker,
 * tooltips...) sometimes try to remove a DOM node that the browser has
 * already moved or destroyed — this happens reliably on Chrome Android
 * when the on-screen keyboard appears, the page auto-scrolls, or the user
 * taps very fast. The browser then throws:
 *
 *   "Failed to execute 'removeChild' on 'Node':
 *    The node to be removed is not a child of this node."
 *
 * That single throw bubbles up through React's reconciliation and unmounts
 * the entire route, leaving a blank screen. Reference issues:
 *   https://github.com/facebook/react/issues/17256
 *   https://github.com/radix-ui/primitives/issues/1859
 *
 * The pragmatic, widely-used workaround is to monkey-patch the two
 * affected DOM methods so that they ignore "not a child" errors. Real
 * bugs (e.g. trying to insert into the wrong parent) still throw and
 * still get caught by our ErrorBoundary.
 *
 * Desktop note: these errors don't happen on desktop Chrome, so the patch
 * is a no-op there.
 */

let _patched = false;

export function installDomSafePatches() {
    if (_patched) return;
    if (typeof window === 'undefined' || typeof Node === 'undefined') return;
    _patched = true;

    const proto = Node.prototype;
    const origRemoveChild = proto.removeChild;
    const origInsertBefore = proto.insertBefore;

    proto.removeChild = function safeRemoveChild(child) {
        try {
            // eslint-disable-next-line prefer-rest-params
            return origRemoveChild.apply(this, arguments);
        } catch (err) {
            if (
                child && child.parentNode !== this
            ) {
                // Already detached / re-parented — silently succeed.
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn('[domSafePatches] swallowed removeChild on detached node');
                }
                return child;
            }
            throw err;
        }
    };

    proto.insertBefore = function safeInsertBefore(newNode, referenceNode) {
        try {
            // eslint-disable-next-line prefer-rest-params
            return origInsertBefore.apply(this, arguments);
        } catch (err) {
            if (referenceNode && referenceNode.parentNode !== this) {
                // Reference node has been moved away — append instead.
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn('[domSafePatches] swallowed insertBefore, falling back to appendChild');
                }
                return this.appendChild(newNode);
            }
            throw err;
        }
    };
}
